import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import type { FollowUpQueueItem } from '@tailoring-crm/shared';
import { FollowUpRow } from '../follow-ups/follow-up-row.js';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  ListSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import { followUpState } from '../../lib/follow-ups/follow-up-state.js';
import { useFollowUpWindow } from '../../lib/follow-ups/use-follow-ups.js';

/**
 * What you owe, on every screen.
 *
 * FOLLOW-UPS USED TO BE A PLACE YOU WENT. That is the wrong shape for them: chasing a customer
 * is not a destination, it is a debt you carry while doing something else, and a queue you have
 * to remember to open is a queue you remember after the customer has bought elsewhere. So it
 * hangs off the header instead, where it can interrupt.
 *
 * THE COUNT IS OVERDUE PLUS TODAY, NEVER TOMORROW. Tomorrow's rows are shown, because seeing
 * what is coming is half the reason to look — but counting them would light the badge every day
 * of the week, and a badge that is always lit is a badge nobody reads. This one can reach zero,
 * and reaching zero is what makes it worth a glance.
 *
 * It is not an inbox: opening the panel does not clear it. The number is work, and work is only
 * cleared by doing it.
 */
export function FollowUpsMenu(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const queue = useFollowUpWindow('mine');

  const items = useMemo(() => queue.data?.items ?? [], [queue.data]);
  const groups = useMemo(() => {
    // `followUpState` is the same classifier the rows colour themselves with, and the window is
    // capped at the end of tomorrow — so inside this panel `upcoming` can only mean tomorrow.
    const buckets: Record<'overdue' | 'today' | 'tomorrow', FollowUpQueueItem[]> = {
      overdue: [],
      today: [],
      tomorrow: [],
    };
    for (const item of items) {
      const state = followUpState(item.dueAt);
      if (state === 'overdue') buckets.overdue.push(item);
      else if (state === 'today') buckets.today.push(item);
      else if (state === 'upcoming') buckets.tomorrow.push(item);
    }
    return buckets;
  }, [items]);

  const overdue = queue.data?.overdue ?? 0;
  const count = overdue + (queue.data?.dueToday ?? 0);

  const sections = [
    { key: 'overdue' as const, label: t('followUps.overdue'), rows: groups.overdue },
    { key: 'today' as const, label: t('followUps.today'), rows: groups.today },
    { key: 'tomorrow' as const, label: t('followUps.tomorrow'), rows: groups.tomorrow },
  ].filter((section) => section.rows.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        // The count is spoken, not just coloured: red-versus-amber is the whole signal otherwise,
        // and that is exactly the distinction a screen reader and a colour-blind user both lose.
        aria-label={count > 0 ? t('header.followUpsDue', { count }) : t('header.followUps')}
        title={t('header.followUps')}
        className="relative flex size-9 shrink-0 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-panel hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent-foreground/60"
      >
        <Bell aria-hidden="true" className="size-5" />
        {count > 0 ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-4 tabular-nums',
              // Solid, not the Badge component's 10% tint — that tint is designed for a white
              // page and all but vanishes against the dark header.
              overdue > 0
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-warning text-warning-foreground',
            )}
          >
            {count > 9 ? '9+' : count}
          </span>
        ) : null}
      </PopoverTrigger>

      <PopoverContent className="flex max-h-[min(32rem,calc(100vh-5rem))] w-[calc(100vw-1.5rem)] flex-col p-0 sm:w-96">
        <p className="border-b border-border px-3 py-2 text-sm font-semibold">
          {t('header.followUps')}
        </p>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {queue.isPending ? (
            <ListSkeleton rows={3} />
          ) : sections.length === 0 ? (
            <p className="px-2 py-8 text-center text-sm text-muted-foreground">
              {t('followUps.empty')}
            </p>
          ) : (
            sections.map((section) => (
              <section key={section.key} className="mb-2 last:mb-0">
                <h2 className="flex items-center justify-between px-1 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <span
                    className={cn(
                      section.key === 'overdue' && 'text-destructive',
                      section.key === 'today' && 'text-amber-700 dark:text-amber-400',
                    )}
                  >
                    {section.label}
                  </span>
                  <span className="tabular-nums">{section.rows.length}</span>
                </h2>
                <ul className="flex flex-col gap-1.5">
                  {section.rows.map((item) => (
                    <FollowUpRow
                      key={item.id}
                      item={item}
                      compact
                      onDone={() => toast({ title: t('followUps.completed'), variant: 'success' })}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        {/*
          The page is still the only place with the shop-wide view and a queue that reaches past
          tomorrow, so the panel always offers the way through to it.
        */}
        <PopoverClose asChild>
          <Link
            to="/follow-ups"
            className="border-t border-border px-3 py-2 text-center text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            {t('followUps.seeAll')}
          </Link>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
}
